/**
 * useCobrancaDetails - Hook para buscar detalhes completos de uma cobrança via RPC
 * 
 * Centraliza todos os dados do modal de detalhes em uma única chamada RPC:
 * cobrança, cliente, criador, meio de pagamento, conta corrente, itens, parcelas e timeline.
 * 
 * Inclui subscription realtime na tabela financeiro_parcelas para atualização
 * automática quando parcelas são pagas via webhook.
 * 
 * Implementa refetch automático com delay (3s e 6s) ao abrir o modal para
 * capturar dados do worker (timeline e metadados de boleto) que chegam com atraso.
 * 
 * @author Módulo Financeiro
 * @version 1.5.0
 * RELEASE: 2026-06-27 v1.5.0 - Adicionado canal realtime em `financeiro_cobrancas`
 *                              (filtro id=eq.cobrancaId) para refletir mudanças de
 *                              status (ex.: PAYMENT_RECEIVED do worker) sem depender
 *                              dos refetches com delay. Corrige defasagem do badge de
 *                              status quando o ChargeDetailsModal está aberto sobre
 *                              o SubscriptionDetailsModal.
 * RELEASE: 2026-03-15 v1.0.0 - Criação do hook centralizado via RPC
 * RELEASE: 2026-03-17 v1.1.0 - Adicionado realtime em financeiro_parcelas para refetch automático
 * RELEASE: 2026-03-17 v1.2.0 - Refetch com delay para timeline/boleto sem Realtime em webhook_logs
 * RELEASE: 2026-06-16 v1.2.1 - Canal realtime único por montagem (sufixo aleatório) + try/catch
 *                              defensivo para evitar erro "cannot add postgres_changes after subscribe()"
 *                              causado por reuso de canal no cache interno do supabase-js sob StrictMode
 *                              ou re-montagens rápidas do modal de edição.
 * RELEASE: 2026-06-18 v1.3.0 - Polling estendido (15s) enquanto a cobrança tiver
 *                              metadata.pending_provider_sync=true, com timeout de 5 min.
 *                              Garante que eventos charge_update_failed do worker DLQ
 *                              (que rodam minutos depois) apareçam na timeline sem
 *                              o usuário precisar fechar e reabrir o modal.
 * RELEASE: 2026-06-21 v1.4.0 - Tipagem inclui `checkout_session_slug` (cobrança) e
 *                              `checkout_mode` (conta corrente), expostos pela RPC
 *                              get_cobranca_details v1.5.5. Permite ao modal usar
 *                              `resolveCheckoutUrl` e respeitar a contingência
 *                              transparente ↔ provider da conta corrente.
 */

import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { createLogger } from '@/lib/logger';

const logger = createLogger('useCobrancaDetails');

// ── Interfaces de retorno da RPC ──

/** Dados da cobrança retornados pela RPC */
export interface CobrancaDetailsCobranca {
  id: string;
  tenant_id: string;
  customer_id: string;
  deal_id: string | null;
  tipo_cobranca: string;
  meio_pagamento_id: string | null;
  conta_corrente_id: string | null;
  descricao: string | null;
  valor_total: number;
  moeda: string | null;
  status: string;
  data_emissao: string;
  data_vencimento: string | null;
  observacoes: string | null;
  numero_parcelas: number | null;
  created_by: string | null;
  provider_id: string | null;
  provider_charge_id: string | null;
  provider_slug: string | null;
  provider_invoice_url: string | null;
  provider_payment_link: string | null;
  created_at: string;
  updated_at: string | null;
  /** Vínculo com financeiro_assinaturas — quando preenchido, a cobrança foi gerada por uma recorrência */
  assinatura_id: string | null;
  /** Número do ciclo da assinatura (1, 2, 3...) — null para cobranças avulsas */
  ciclo_numero: number | null;
  /** v1.5.4 da RPC: juros/multa configurados na própria cobrança (header) */
  juros: {
    enabled?: boolean;
    multa_tipo?: 'percent' | 'fixed';
    multa_valor?: number;
    juros_tipo?: 'percent' | 'fixed';
    juros_valor?: number;
  } | null;
  multa: Record<string, unknown> | null;
  /** Dias após vencimento em que o link/boleto expira (1..60) */
  dias_expiracao_pos_vencimento: number | null;
  /** v1.4.0 — slug ativo do checkout transparente (cache em financeiro_cobrancas) */
  checkout_session_slug: string | null;
  /** v1.7.0 (2026-09-12): liga/desliga da régua de cobrança (avaliada server-side no disparo) */
  reminders_enabled: boolean;
}



/** Dados do cliente retornados pela RPC */
export interface CobrancaDetailsCustomer {
  name: string | null;
  customer_type: string;
  cpf: string | null;
  cnpj: string | null;
  email: string | null;
  phone: string | null;
}

/** Dados do criador retornados pela RPC */
export interface CobrancaDetailsCreator {
  full_name: string | null;
  avatar_url: string | null;
}

/** Dados do meio de pagamento retornados pela RPC */
export interface CobrancaDetailsMeioPagamento {
  id: string;
  nome: string;
  tipo_pagamento: string;
}

/** Dados da conta corrente retornados pela RPC */
export interface CobrancaDetailsContaCorrente {
  id: string;
  nome: string;
  banco_nome: string | null;
  tipo_conta: string | null;
  provider_slug: string | null;
  agencia: string | null;
  conta: string | null;
  /** v1.4.0 — modo de checkout preferido configurado na conta corrente */
  checkout_mode: 'transparent' | 'provider' | null;
}

/** Item da cobrança retornado pela RPC */
export interface CobrancaDetailsItem {
  id: string;
  descricao: string;
  quantidade: number;
  valor_unitario: number;
  desconto: number;
  total: number | null;
  product_id: string | null;
  product_name: string | null;
  /**
   * v1.8.0 da RPC get_cobranca_details — indica a linha automática
   * "Ajuste de parcelas" (somente leitura, nunca reenviada na edição).
   */
  is_ajuste_parcelas?: boolean;
}

/** Parcela retornada pela RPC */
export interface CobrancaDetailsParcela {
  id: string;
  numero_parcela: number;
  valor: number;
  data_vencimento: string;
  data_pagamento: string | null;
  valor_pago: number | null;
  status: string;
  /** Metadados da parcela (contém provider_payment_id, etc.) */
  metadata: Record<string, unknown> | null;
  /** Número da fatura no provider (ex: Asaas invoiceNumber) — coluna dedicada */
  numero_fatura: string | null;
  // v1.5.0: campos de baixa manual / cancelamento
  paid_source?: 'manual' | 'provider_webhook' | 'system' | null;
  paid_by_user_id?: string | null;
  paid_by_user_name?: string | null;
  paid_meio_pagamento_id?: string | null;
  paid_meio_pagamento_nome?: string | null;
  paid_meio_pagamento_tipo?: string | null;
  paid_description?: string | null;
  paid_receipt_path?: string | null;
  cancel_reason?: string | null;
  cancelled_by_user_id?: string | null;
  cancelled_by_user_name?: string | null;
  cancelled_at?: string | null;
}

/** Evento de timeline retornado pela RPC */
export interface CobrancaDetailsTimelineEvent {
  id: string;
  event_type: string;
  created_at: string;
  processing_status: string | null;
  /** Mensagem de erro quando processing_status === 'failed' */
  error_message?: string | null;
  /** ID do pagamento no provider (ex: pay_xxx) — identifica a parcela */
  provider_payment_id?: string | null;
  /** Número da fatura no provider (ex: 13488108) — extraído do raw_payload */
  invoice_number?: string | null;
  /** Diff de campos alterados em eventos de edição (charge_updated / PAYMENT_UPDATED) */
  change_diff?: Record<string, { old: unknown; new: unknown }> | null;
  // v1.5.0: origem do evento (manual vs provider) e quem executou
  event_source?: 'provider_webhook' | 'manual' | 'system' | null;
  provider_slug?: string | null;
  actor_user_id?: string | null;
  actor_full_name?: string | null;
  parcela_id?: string | null;
  raw_payload?: Record<string, unknown> | null;
}


/** Dados da assinatura vinculada (quando a cobrança foi gerada por recorrência) */
export interface CobrancaDetailsAssinatura {
  id: string;
  frequencia: string | null;
  tipo_prazo: string | null;
  ciclos_total: number | null;
  ciclos_gerados: number | null;
  status: string | null;
  proximo_vencimento: string | null;
  /** Regra de emissão herdada — v1.5.2 da RPC */
  tipo_emissao: 'dias_antes' | 'dia_fixo' | string | null;
  dias_antes_emissao: number | null;
  dia_emissao: number | null;
  /** Juros/Multa herdados — v1.5.3 da RPC (read-only no modal de cobrança) */
  juros: {
    enabled?: boolean;
    multa_tipo?: 'percent' | 'fixed';
    multa_valor?: number;
    juros_tipo?: 'percent' | 'fixed';
    juros_valor?: number;
  } | null;
  multa: number | null;
  dias_expiracao_pos_vencimento: number | null;
}

/** Estrutura completa retornada pela RPC get_cobranca_details */
export interface CobrancaDetailsResult {
  cobranca: CobrancaDetailsCobranca;
  assinatura: CobrancaDetailsAssinatura | null;
  customer: CobrancaDetailsCustomer | null;
  creator: CobrancaDetailsCreator | null;
  meio_pagamento: CobrancaDetailsMeioPagamento | null;
  conta_corrente: CobrancaDetailsContaCorrente | null;
  itens: CobrancaDetailsItem[];
  parcelas: CobrancaDetailsParcela[];
  timeline: CobrancaDetailsTimelineEvent[];
}

/**
 * Hook para buscar detalhes completos de uma cobrança via RPC centralizada.
 * Inclui realtime subscription em financeiro_parcelas para atualização
 * automática quando parcelas são pagas.
 * 
 * @param cobrancaId - UUID da cobrança
 * @param open - Se o modal está aberto (controla enabled da query e subscription)
 * @returns Dados tipados da cobrança com loading/error states
 */
export function useCobrancaDetails(cobrancaId: string | undefined | null, open: boolean) {
  const queryClient = useQueryClient();

  // ── Subscription realtime em financeiro_parcelas ──
  // Escuta INSERT, UPDATE e DELETE para cobrir todos os cenários do worker
  // (criação de parcelas, atualização de metadata com bank_slip_url, etc.)
  useEffect(() => {
    if (!cobrancaId || !open) return;

    // Sufixo aleatório garante nome de canal único por montagem do efeito.
    // Evita race condition do cache interno do supabase-js que, sob StrictMode
    // ou re-abertura rápida do modal, retorna um canal já subscrito ao chamar
    // `.channel(name)` novamente — e então `.on(...)` lança a exceção
    // "cannot add postgres_changes callbacks after subscribe()".
    const channelName = `parcelas-rt-${cobrancaId}-${crypto.randomUUID()}`;
    let channel: ReturnType<typeof supabase.channel> | null = null;

    try {
      channel = supabase
        .channel(channelName)
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'financeiro_parcelas',
            filter: `cobranca_id=eq.${cobrancaId}`,
          },
          () => {
            logger.debug('Parcela alterada via realtime, invalidando cache', { cobrancaId });
            queryClient.invalidateQueries({ queryKey: ['cobranca-details', cobrancaId] });
          }
        )
        .subscribe();
    } catch (err) {
      // Defensivo: não propagar para o ErrorBoundary; realtime é opcional
      // (os refetches com delay abaixo garantem atualização eventual).
      logger.warn('Falha ao inscrever canal realtime de parcelas', {
        cobrancaId,
        error: err instanceof Error ? err.message : String(err),
      });
    }

    return () => {
      if (channel) {
        supabase.removeChannel(channel);
      }
    };
  }, [cobrancaId, open, queryClient]);

  // ── v1.5.0: Subscription realtime em financeiro_cobrancas ──
  // O worker (financial-worker-pgmq) atualiza primeiro a cobrança ao processar
  // PAYMENT_RECEIVED/CONFIRMED do gateway. Sem esse listener, o status no modal
  // só mudaria pelos refetches agendados (3s/6s) ou pelo polling DLQ, gerando
  // defasagem perceptível quando o ChargeDetailsModal está aberto.
  useEffect(() => {
    if (!cobrancaId || !open) return;

    const channelName = `cobranca-rt-${cobrancaId}-${crypto.randomUUID()}`;
    let channel: ReturnType<typeof supabase.channel> | null = null;

    try {
      channel = supabase
        .channel(channelName)
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'financeiro_cobrancas',
            filter: `id=eq.${cobrancaId}`,
          },
          () => {
            logger.debug('Cobrança alterada via realtime, invalidando cache', { cobrancaId });
            queryClient.invalidateQueries({ queryKey: ['cobranca-details', cobrancaId] });
            queryClient.invalidateQueries({ queryKey: ['financeiro-cobrancas'] });
            queryClient.invalidateQueries({ queryKey: ['financeiro-cobrancas-paginated'] });
          }
        )
        .subscribe();
    } catch (err) {
      // Defensivo: realtime é opcional; fallback nos refetches agendados.
      logger.warn('Falha ao inscrever canal realtime de cobrança', {
        cobrancaId,
        error: err instanceof Error ? err.message : String(err),
      });
    }

    return () => {
      if (channel) {
        supabase.removeChannel(channel);
      }
    };
  }, [cobrancaId, open, queryClient]);

  // ── Refetch com delay para dados do worker (timeline + metadados boleto) ──
  // O worker processa webhooks PAYMENT_CREATED com atraso de 1-5s após criação.
  // Ao invés de Realtime em financeiro_webhook_logs (alto volume de WAL),
  // agendamos 2 refetches automáticos (3s e 6s) para capturar esses dados.
  useEffect(() => {
    if (!cobrancaId || !open) return;

    const timer1 = setTimeout(() => {
      logger.debug('Refetch delayed #1 (3s) para dados do worker', { cobrancaId });
      queryClient.invalidateQueries({ queryKey: ['cobranca-details', cobrancaId] });
    }, 3000);

    const timer2 = setTimeout(() => {
      logger.debug('Refetch delayed #2 (6s) para dados do worker', { cobrancaId });
      queryClient.invalidateQueries({ queryKey: ['cobranca-details', cobrancaId] });
    }, 6000);

    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
    };
  }, [cobrancaId, open, queryClient]);

  // ── v1.3.0: Polling estendido para resultado do worker DLQ ──
  // O worker financial-charge-worker pode demorar alguns minutos para processar
  // mensagens de update_payment e gravar charge_update_failed na timeline em caso
  // de falha (HTTP 400 do provider). Os refetches em 3s/6s não cobrem este janela.
  // Estratégia: enquanto a cobrança estiver com metadata.pending_provider_sync=true,
  // refazemos a query a cada 15s, com timeout máximo de 5 min (segurança).
  useEffect(() => {
    if (!cobrancaId || !open) return;

    const startedAt = Date.now();
    const MAX_DURATION_MS = 5 * 60 * 1000; // 5 minutos
    const POLL_INTERVAL_MS = 15 * 1000;    // 15 segundos

    const interval = setInterval(() => {
      // Limite de duração — para não martelar a RPC indefinidamente
      if (Date.now() - startedAt > MAX_DURATION_MS) {
        clearInterval(interval);
        logger.debug('Polling estendido encerrado por timeout', { cobrancaId });
        return;
      }

      // Lê o cache atual; se pending_provider_sync sumiu, para o polling
      const cached = queryClient.getQueryData<CobrancaDetailsResult>([
        'cobranca-details',
        cobrancaId,
      ]);
      const cobrancaCached = cached?.cobranca as unknown as (Record<string, unknown> | undefined);
      const metadata = (cobrancaCached?.metadata ?? null) as Record<string, unknown> | null;
      const isPending = metadata?.pending_provider_sync === true;

      if (!isPending) {
        clearInterval(interval);
        logger.debug('Polling estendido encerrado (sync concluída)', { cobrancaId });
        return;
      }

      logger.debug('Refetch polling 15s — pending_provider_sync=true', { cobrancaId });
      queryClient.invalidateQueries({ queryKey: ['cobranca-details', cobrancaId] });
    }, POLL_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [cobrancaId, open, queryClient]);


  return useQuery({
    queryKey: ['cobranca-details', cobrancaId],
    queryFn: async (): Promise<CobrancaDetailsResult> => {
      if (!cobrancaId) {
        throw new Error('cobrancaId é obrigatório');
      }

      const { data, error } = await supabase.rpc('get_cobranca_details', {
        p_cobranca_id: cobrancaId,
      });

      if (error) {
        logger.error('Erro ao buscar detalhes da cobrança via RPC', { error: error.message });
        throw error;
      }

      // A RPC retorna jsonb, o Supabase deserializa como objeto JS
      return data as unknown as CobrancaDetailsResult;
    },
    enabled: !!cobrancaId && open,
  });
}
